<template>
  <v-container fluid>
    <v-card>
      <v-app-bar
        absolute
        elevate-on-scroll
        scroll-target="#scrolling-techniques-7"
      >
        <v-spacer></v-spacer>
        <v-list-item-action class="mr-1">
          <v-btn
            icon
            outlined
            title="Создать"
            :class="{ 'primary--text': isDraw }"
            @click="onCreate"
            :disabled="editObj"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-list-item-action>

        <v-list-item-action class="mr-1 ml-0">
          <v-btn
            icon
            outlined
            title="Редактировать"
            :class="{ 'primary--text': editObj }"
            @click="onEdit"
            :disabled="isDraw"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </v-list-item-action>
      </v-app-bar>

      <v-sheet
        id="scrolling-techniques-7"
        class="overflow-x-hidden"
        max-height="90vh"
      >
        <v-row style="height: 60px"></v-row>
        <v-row dense>
          <v-col align="center" justify="center">
            <vl-map
              data-projection="EPSG:4326"
              style="width: 100%; height: 100vh;"
              :key="mapChange"
              ref="map"
            >
              <vl-view :zoom.sync="zoom" :center.sync="center"></vl-view>

              <vl-layer-tile
                v-for="(item, index) in $store.state.settings.maps"
                :z-index="index"
                :opacity="item.alpha / 100"
                :key="item.id"
              >
                <vl-source-osm
                  v-if="(item.id === 'osm') & item.visible"
                ></vl-source-osm>
                <vl-source-xyz
                    v-if="item.id === 'googleHybrid' && item.visible"
                    url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
                ></vl-source-xyz>
                <vl-source-xyz
                  v-if="(item.id === '2gis') & item.visible"
                  url="http://tile0.maps.2gis.com/tiles?x={x}&y={y}&z={z}&v=1&ts=online_sd"
                ></vl-source-xyz>
                <vl-source-bingmaps
                  v-if="(item.id === 'bingMapsAerial') & item.visible"
                  :api-key="$store.state.settings.apiKeyBing"
                  imagery-set="Aerial"
                ></vl-source-bingmaps>
                <vl-source-bingmaps
                  v-if="(item.id === 'bingMapsAerialWithLabels') & item.visible"
                  :api-key="$store.state.settings.apiKeyBing"
                  imagery-set="Aerial"
                ></vl-source-bingmaps>
                <vl-source-xyz
                  v-if="(item.id === 'mapbox') & item.visible"
                  url="https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoiZGFnb3QiLCJhIjoiY2lveTNjbnRnMDBjOXZtbTJqNGNsNmk1NCJ9.bvTUlzXKG4A2ZejsF1v2Hw"
                ></vl-source-xyz>
                <vl-source-xyz
                  v-if="(item.id === 'arcgis') & item.visible"
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  :tile-load-function="arcgisLoad"
                ></vl-source-xyz>
              </vl-layer-tile>

              <vl-layer-vector :z-index="102">
                <vl-source-vector :loader="getDistrictsData" />
                <vl-style-func :factory="districtStyle" />
              </vl-layer-vector>

              <vl-layer-vector :z-index="100" id="sanitaryLayer">
                <vl-source-vector :loader="getSanitaryGrid" />
                <vl-style-func :factory="styleFunction" />
              </vl-layer-vector>
              <vl-layer-vector :z-index="101" id="sanitaryDraw">
                <vl-source-vector
                  :features.sync="drawFeatures"
                  ident="featuresDraw"
                ></vl-source-vector>
              </vl-layer-vector>
              <vl-interaction-draw
                v-if="isDraw"
                type="Polygon"
                source="featuresDraw"
                @drawend="drawEnd"
                stop-click
              />
              <vl-interaction-select
                :features.sync="selectedFeatures"
                :layers="['sanitaryLayer', 'sanitaryDraw']"
                @select="onSelect"
                @unselect="onUnselect"
                ident="featuresSelected"
                ref="select"
              />
              <vl-interaction-modify
                      source="featuresSelected"
                      @modifyend="onModifyEnd"
              />
            </vl-map>
          </v-col>
          <SanitaryGridPassport
            v-if="passport"
            :setSanitaryGrid="setSanitaryGrid"
            :properties="properties"
            :isDraw="isDraw"
            @remove-sanitary="removeSanitaryGrid"
          />
        </v-row>
      </v-sheet>
      <v-card>
        <v-snackbar v-model="snackbar" :timeout="timeout" top right outlined>
          {{ text }}
          <template v-slot:action="{ attrs }">
            <v-btn color="blue" text v-bind="attrs" @click="snackbar = false">
              Закрыть
            </v-btn>
          </template>
        </v-snackbar>
      </v-card>
    </v-card>
  </v-container>
</template>

<script>
import WKT from "ol/format/WKT";
import GeoJSON from "ol/format/GeoJSON";
import { mapActions } from "vuex";
import { Style, Stroke, Fill, Text } from "ol/style";
import SanitaryGridPassport from "../passports/SanitaryGridPassport.vue";
import TileState from "ol/TileState";

export default {
  name: "SanitaryGridTab",

  data() {
    return {
      mapChange: 0,
      zoom: 13,
      center: [76.889709, 43.238949],
      drawFeatures: [],
      selectedFeatures: [],
      formatWKT: new WKT(),
      formatGeoJSON: new GeoJSON(),
      passport: false,
      properties: {},
      isDraw: false,
      editObj: false,
      snackbar: false,
      text: "",
      timeout: 2000,
      geometryModify: null
    };
  },

  methods: {
    ...mapActions([
      "getDistrictsData",
      "getSanitaryGrid",
      "getSanitaryGridById",
      "createSanitaryGrid",
      "pushSanitaryGrid",
      "destroySanitaryGrid"
    ]),
    arcgisLoad(imageTile, src) {
      let map = this.$refs.map;
      let zoom = map.getView().getZoom();
      if (zoom > 19) {
        imageTile.setState(TileState.ERROR);
      } else {
        imageTile.getImage().src = src;
      }
    },
    districtStyle() {
      return () => {
        let baseStyle = new Style({
          stroke: new Stroke({
            color: "rgba(0,0,255, 1)",
            width: 3
          })
        });
        return [baseStyle];
      };
    },
    styleFunction() {
      return feature => {
        let baseStyle = new Style({
          fill: new Fill({
            color: String(feature.get("color"))
          }),
          stroke: new Stroke({
            color: "rgba(0,0,255, 1)",
            width: 1
          }),
          text: new Text({
            text: feature.get("name")
              ? String(feature.get("name")) +
                ": " +
                String(feature.get("count"))
              : String(feature.get("count")),
            scale: 1.5
          })
        });
        return [baseStyle];
      };
    },
    async setSanitaryGrid({
      name,
      color,
      pesticides,
      company,
      comment,
      begin_date,
      end_date
    }) {
      let feature = this.formatGeoJSON.readFeature(this.selectedFeatures[0]);
      let wktRepresenation = this.formatWKT.writeFeature(feature);
      let properties = this.selectedFeatures[0].properties;
      if (properties) {
        if (this.geometryModify !== null)
          wktRepresenation = this.geometryModify;
        await this.pushSanitaryGrid({
          id: properties.id,
          geometry: wktRepresenation,
          name: name,
          color: color,
          pesticides: pesticides,
          company: company,
          comment: comment,
          begin_date: begin_date,
          end_date: end_date
        });
        this.geometryModify = null;
      } else {
        await this.createSanitaryGrid({
          geometry: wktRepresenation,
          name: name,
          color: color.hexa,
          pesticides: pesticides,
          company: company,
          comment: comment,
          begin_date: begin_date,
          end_date: end_date
        });
      }
      setTimeout(() => {
        this.drawFeatures = [];
        this.passport = false;
        this.text = this.$store.state.message.msg;
        this.snackbar = true;
        this.mapChange++;
      }, 1000);
    },
    async removeSanitaryGrid() {
      let properties = this.selectedFeatures[0].properties;
      if (properties) await this.destroySanitaryGrid({ id: properties.id });
      setTimeout(() => {
        this.drawFeatures = [];
        this.passport = false;
        this.text = this.$store.state.message.msg;
        this.snackbar = true;
        this.mapChange++;
      }, 1000);
    },
    drawEnd() {
      this.selectedFeatures = [];
      this.selectedFeatures.push(
        this.drawFeatures[this.drawFeatures.length - 1]
      );
    },
    async onSelect(feature) {
      if (this.isDraw || this.editObj) {
        let props;
        if (!this.passport) this.passport = true;
        if (!feature.json.properties) {
          props = { type_id: "Polygon" };
        } else {
          props = await this.getSanitaryGridById(feature.json.properties.id);
        }
        props.geometry = feature.json.geometry;
        setTimeout(() => {
          if (JSON.stringify(this.properties) === JSON.stringify({})) {
            this.properties = props;
          } else if (feature.json.properties)
            if (feature.json.properties.id) this.properties = props;
        }, 1);
      }
    },
    onUnselect(feature) {
      if (feature.json.properties) {
        this.properties = {};
        this.passport = false;
      }
    },
    onCreate() {
      this.isDraw = !this.isDraw;
      if (!this.isDraw) {
        this.drawFeatures = [];
        this.selectedFeatures = [];
        this.passport = false;
      }
    },
    onEdit() {
      this.editObj = !this.editObj;
      if (this.editObj) {
        this.$refs.select.setActive(true);
      } else {
        this.$refs.select.setActive(false);
        this.selectedFeatures = [];
      }
    },
    onModifyEnd(evt) {
      let feature = this.formatGeoJSON.readFeature(evt.json[0].geometry);
      this.geometryModify = this.formatWKT.writeFeature(feature);
    }
  },

  async mounted() {
    this.$refs.select.setActive(false);

  },

  components: { SanitaryGridPassport }
};
</script>

<style scoped>
::v-deep .v-list-item__action {
  margin: 0;
}
</style>
